// Library Methods
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";

// Styles
import {
  PageContainerWrapper,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  ThemeProvider as LightThemeProvider,
  useTheme,
} from "@mui/material/styles";
import Loader from "src/components/shared/loaders/Loader/Loader";
import Breadcrumb from "src/components/containers/shared/Breadcrumb/Breadcrumb";
import SnackbarMessage from "src/components/shared/alerts/SnackbarMessage/SnackbarMessage";
import ReportButtons from "src/components/containers/shared/ReportButtons/ReportButtons";
import PrintButton from "src/components/containers/shared/ReportButtons/PrintButton/PrintButton";
import ReportError from "src/components/containers/shared/ReportError/ReportError";
import CompleteExamReports from "./CompleteExamReports/CompleteExamReports";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";
import { ReportContext } from "src/contexts/reports/ReportContext";

// Config
import { priorityLanguageMap } from "src/config/languages";

// Utils
import { getTruncatedText } from "src/utils/text/textHelper";
import { setupTheme } from "src/utils/theme/themeHelper";

// Hooks
import { usePatientById } from "src/services/patients/hooks/usePatientById";
import { useExamById } from "src/services/exams/hooks/useExamById";
import { useReportInfo } from "src/services/exams/hooks/exam-reports/useReportInfo";
import { useExamAccessInfo } from "src/services/exams/hooks/exam-reports/useExamAccessInfo";
import { useGenerateReportFile } from "src/services/exams/hooks/exam-reports/useGenerateReportFile";
import useReportValidation from "src/hooks/reports/useReportValidation";
import useReportGeneration from "src/hooks/reports/useReportGeneration";

// Types
import { ReportInfoType } from "src/services/exams/types";
import { ReportFileType } from "src/constants/enums";

const ExamReport = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { clinicInfo } = useClinicContext();
  const { snackbarInfo, setSnackbarInfo } = useSnackbarContext();

  // The report is always printed/exported on a fixed white page, regardless
  // of the app's light/dark setting — locking descendants to the light theme
  // here (rather than patching every Report* component's colors individually)
  // keeps that page legible no matter what mode the rest of the app is in.
  const languageKey = i18n.language as keyof typeof priorityLanguageMap;
  const lightTheme = useMemo(
    () => setupTheme(theme.direction, languageKey, "light"),
    [theme.direction, languageKey]
  );

  const [initialReportData, setInitialReportData] = useState<
    Array<ReportInfoType>
  >([]);

  const location = useLocation();
  const patientId = location?.pathname?.split("/")?.[2];
  const examId = location?.pathname?.split("/")?.[4];

  const {
    data: patientData,
    isLoading: isLoadingPatient,
    isFetching: isFetchingPatient,
    isRefetching: isRefetchingPatient,
  } = usePatientById(clinicInfo?.id, {
    patientId,
  });

  const {
    data: examData,
    isLoading: isLoadingExam,
    isFetching: isFetchingExam,
    isRefetching: isRefetchingExam,
  } = useExamById(clinicInfo?.id, {
    patientId,
    examId,
  });

  const { shouldDisplayReport, isValidating, reportData, refetchComment } =
    useReportValidation({
      patientId,
      exam: examData,
    });

  const isLoading = useMemo(
    () =>
      isLoadingPatient ||
      isFetchingPatient ||
      isRefetchingPatient ||
      isLoadingExam ||
      isFetchingExam ||
      isRefetchingExam ||
      isValidating,
    [
      isLoadingPatient,
      isFetchingPatient,
      isRefetchingPatient,
      isLoadingExam,
      isFetchingExam,
      isRefetchingExam,
      isValidating,
    ]
  );

  const formattedPatientName = useMemo(
    () =>
      getTruncatedText(`${patientData?.firstName} ${patientData?.lastName}`),
    [patientData]
  );

  const { mutateAsync: getReportInfo } = useReportInfo(clinicInfo?.id);
  const { mutateAsync: getAccessInfo } = useExamAccessInfo(clinicInfo?.id);
  const { mutateAsync: generateReportFile } = useGenerateReportFile(
    clinicInfo?.id
  );

  const getReportInfoWrapper = useCallback(
    async () => await getReportInfo({ patientId, examId }),
    [patientId, examId, getReportInfo]
  );

  const getAccessInfoWrapper = useCallback(
    async (fileType: ReportFileType) =>
      await getAccessInfo({ patientId, examId, fileType }),
    [patientId, examId, getAccessInfo]
  );

  const generateReportFileWrapper = useCallback(
    async (fileType: ReportFileType) =>
      await generateReportFile({ patientId, examId, fileType }),
    [patientId, examId, generateReportFile]
  );

  // on page load, check if the report exists
  useEffect(() => {
    if (!clinicInfo?.id) return;

    getReportInfoWrapper().then((initialData) => {
      setInitialReportData(initialData);
    });
  }, [clinicInfo?.id, getReportInfoWrapper]);

  const reportGenerationProps = useReportGeneration({
    getReportInfo: getReportInfoWrapper,
    getAccessInfo: getAccessInfoWrapper,
    generateReportFile: generateReportFileWrapper,
  });

  // if the report doesn't exist, don't show the download buttons
  const showDownloadButtons = useMemo(
    () => Boolean(initialReportData?.length > 0 && shouldDisplayReport),
    [initialReportData, shouldDisplayReport]
  );

  return (
    <ReportContext.Provider
      value={{
        progressionAnalysis: undefined,
        externalExam: undefined,
        patient: patientData,
        exam: examData,
        reportData,
        refetchProgressionAnalysisComment: async () => {},
        refetchExamComment: refetchComment,
        getReportInfoData: reportGenerationProps.getReportInfoData,
      }}
    >
      <LightThemeProvider theme={lightTheme}>
        <Box
          sx={{
            ...PageContainerWrapper(theme),
            backgroundColor: "#FFF",
            // MuiTypography forces `color: inherit`, so plain text only
            // picks up a theme's palette via CSS inheritance — the nested
            // LightThemeProvider above doesn't touch that chain by itself,
            // so it must be seeded here explicitly for it to cascade.
            color: getSurfaceTokens().onSurface,
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography fontSize="16px" fontWeight={600}>
            {t("Exam Report")}
          </Typography>

          <Breadcrumb
            isLoading={isLoading}
            breadcrumbLink={{
              href: "/patients",
              title: `${t("Back to Exam List")} (${formattedPatientName})`,
            }}
            breadcrumbParts={[]}
            breadcrumbState={{ stateId: patientData?.id, fromReport: true }}
            optionalButton={
              <PrintButton
                isLoading={isLoading}
                showDownloadButtons={showDownloadButtons}
                fileInfo={reportGenerationProps.fileInfo.PDF}
                reportInfoError={reportGenerationProps.reportInfoError}
                printFile={reportGenerationProps.printFile}
              />
            }
          />

          <ReportButtons
            patient={patientData}
            examId={examId}
            isLoading={isLoading}
            showTransferButton={true}
            showDownloadButtons={showDownloadButtons}
            reportGenerationProps={reportGenerationProps}
          />

          {isLoading && (
            <Box sx={{ padding: "24px" }}>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                py={10}
                px="24px"
                gap="24px"
                textAlign="center"
              >
                <Typography fontSize="24px" fontWeight={600}>
                  {t("Loading Your Report...")}
                </Typography>
                <Loader containerProps={{ height: "100px" }} />
              </Box>
            </Box>
          )}
          {!isLoading && !shouldDisplayReport && (
            <ReportError title={t("Unable to Retrieve Your Report.")} />
          )}
          {!isLoading && shouldDisplayReport && <CompleteExamReports />}

          {/* Snackbar success and error messages */}
          <SnackbarMessage
            snackbarInfo={snackbarInfo}
            setSnackbarInfo={setSnackbarInfo}
          />
        </Box>
      </LightThemeProvider>
    </ReportContext.Provider>
  );
};

export default ExamReport;
